<template>
  <section v-if="canRead" class="border rounded p-3 mt-3" aria-label="Zahlungsbedingungen importieren und exportieren">
    <div class="d-flex justify-content-between align-items-center gap-2">
      <h6 class="mb-0">Zahlungsbedingungen importieren / exportieren</h6>
      <button class="btn btn-outline-success btn-sm" :disabled="busy || !isCurrentCompany" @click="exportFile">Zahlungsbedingungen exportieren (JSON)</button>
    </div>
    <div v-if="!isCurrentCompany" class="alert alert-info mt-3 mb-0">
      Wählen Sie oben in der Anwendung zuerst die Firma „{{ companyName }}“ aus.
    </div>
    <template v-else-if="canWrite">
      <p class="small text-muted mt-2">Alle ausgewählten Änderungen werden gemeinsam übernommen. Fehler müssen vorher korrigiert und Konflikte ausdrücklich entschieden werden.</p>
      <label for="paymentTermImportFile" class="form-label">Zahlungsbedingungen-Datei (JSON, höchstens 1 MiB)</label>
      <input :key="fileKey" id="paymentTermImportFile" type="file" class="form-control" accept=".json,application/json" :disabled="busy" @change="selectFile" />
      <div v-if="canSetDefault" class="form-check mt-2">
        <input id="paymentTermImportDefault" v-model="applyDefaultFromFile" class="form-check-input" type="checkbox" :disabled="busy" @change="invalidatePreview" />
        <label for="paymentTermImportDefault" class="form-check-label">Standard aus Datei ausdrücklich übernehmen</label>
      </div>
      <p class="small text-muted mt-1">Ohne diese Auswahl bleibt der bisherige Standard erhalten. Neue Zahlungsbedingungen werden dann ohne Standardstatus angelegt.</p>
      <button class="btn btn-outline-primary btn-sm mt-2" :disabled="!content || busy" @click="createPreview">{{ preview ? 'Vorschau aktualisieren' : 'Importvorschau erstellen' }}</button>
      <div v-if="preview" class="mt-3">
        <div v-if="previewStale" class="alert alert-info">Die Auswahl wurde geändert. Bitte die Vorschau aktualisieren.</div>
        <ul v-if="preview.errors?.length" class="alert alert-danger ps-4" role="alert"><li v-for="(message, index) in preview.errors" :key="index">{{ message }}</li></ul>
        <div v-if="preview.rows?.some(row => row.existingId)" class="d-flex flex-wrap gap-2 mb-2">
          <button class="btn btn-outline-secondary btn-sm" :disabled="busy" @click="decideAll('update')">Alle vorhandenen aktualisieren</button>
          <button class="btn btn-outline-secondary btn-sm" :disabled="busy" @click="decideAll('skip')">Alle vorhandenen überspringen</button>
        </div>
        <div class="table-responsive">
          <table class="table table-sm align-middle">
            <thead><tr><th>Zeile</th><th>Zahlungsbedingung</th><th>Tage</th><th>Aktion</th><th>Prüfergebnis</th></tr></thead>
            <tbody><tr v-for="row in preview.rows" :key="row.row">
              <td>{{ row.row }}</td>
              <td>{{ row.name || '–' }}<div v-if="row.textTemplate" class="small text-muted text-break">{{ row.textTemplate }}</div><div v-if="row.importedDefault" class="small">Standard in der Datei</div></td>
              <td>{{ row.daysUntilDue ?? '–' }}</td>
              <td>
                <select v-if="row.existingId" v-model="actions[row.row]" class="form-select form-select-sm" :aria-label="`Konfliktentscheidung Zeile ${row.row}`" :disabled="busy" @change="invalidatePreview">
                  <option value="">Bitte entscheiden</option><option value="update">Aktualisieren</option><option value="skip">Überspringen</option>
                </select>
                <span v-else>{{ actionLabel(row.action) }}</span>
              </td>
              <td><div v-for="(message, index) in row.errors" :key="`error-${index}`" class="text-danger">{{ message }}</div><div v-for="(message, index) in row.warnings" :key="`warning-${index}`" class="text-muted small">{{ message }}</div><div v-if="row.action === 'conflict' && !actions[row.row]" class="text-warning">Vorhanden: {{ row.existingName }}. Bitte Aktion wählen.</div></td>
            </tr></tbody>
          </table>
        </div>
        <p class="mb-2">Neu: {{ preview.created }} · Aktualisieren: {{ preview.updated }} · Ausdrücklich überspringen: {{ preview.skipped }}</p>
        <div v-if="preview.defaultChange" class="alert alert-warning">{{ preview.defaultChange }}</div>
        <button class="btn btn-primary" :disabled="busy || previewStale || !preview.canApply || !preview.previewToken" @click="applyImport">Geprüfte Änderungen übernehmen</button>
      </div>
    </template>
    <div v-if="error" class="alert alert-danger mt-3 mb-0" role="alert">{{ error }}</div>
    <div v-if="result" class="alert alert-success mt-3 mb-0" role="status">Import abgeschlossen: {{ result.created }} neu, {{ result.updated }} aktualisiert, {{ result.skipped }} ausdrücklich übersprungen.</div>
  </section>
</template>

<script>
import CompanyService from "@/services/CompanyService";
import { MAX_PAYMENT_TERM_FILE_BYTES, buildPaymentTermImportInput, paymentTermImportError, paymentTermExportError } from "@/utils/paymentTermImport.cjs";

export default {
  name: "PaymentTermImportPanel",
  props: {
    companyId: { type: String, required: true }, companyName: { type: String, default: "" },
    currentCompanyId: { type: String, default: "" }, canRead: Boolean, canWrite: Boolean, canSetDefault: Boolean,
  },
  emits: ["imported"],
  data: () => ({ content: "", actions: {}, applyDefaultFromFile: false, preview: null, reviewedInput: null,
    previewStale: false, busy: false, error: "", result: null, generation: 0, fileKey: 0 }),
  computed: { isCurrentCompany() { return this.companyId.toLowerCase() === (this.currentCompanyId || "").toLowerCase(); } },
  watch: {
    companyId() { this.reset(); }, currentCompanyId() { this.reset(); },
    canSetDefault(value) { if (!value) { this.applyDefaultFromFile = false; this.invalidatePreview(); } },
  },
  beforeUnmount() { this.generation++; },
  methods: {
    reset() {
      this.generation++; this.fileKey++; this.content = ""; this.actions = {}; this.applyDefaultFromFile = false;
      this.preview = null; this.reviewedInput = null; this.previewStale = false; this.busy = false; this.error = ""; this.result = null;
    },
    invalidatePreview() {
      this.reviewedInput = null;
      if (this.preview) { this.preview.previewToken = null; this.previewStale = true; }
      this.result = null;
    },
    async selectFile(event) {
      const file = event.target.files?.[0];
      this.generation++; const generation = this.generation;
      this.content = ""; this.actions = {}; this.preview = null; this.reviewedInput = null; this.result = null; this.error = "";
      if (!file) return;
      if (file.size > MAX_PAYMENT_TERM_FILE_BYTES) { this.error = "Die Datei darf höchstens 1 MiB groß sein."; return; }
      try { const content = await file.text(); if (generation === this.generation) this.content = content; }
      catch { if (generation === this.generation) this.error = "Die Datei konnte nicht gelesen werden."; }
    },
    async createPreview() {
      if (!this.canWrite || !this.canRead || !this.isCurrentCompany || this.busy || !this.content) return;
      const generation = ++this.generation;
      const input = buildPaymentTermImportInput(this.content, this.actions, this.canSetDefault && this.applyDefaultFromFile);
      this.busy = true; this.error = ""; this.result = null; this.invalidatePreview();
      try {
        const response = await CompanyService.previewPaymentTermImport(this.companyId, input);
        if (generation !== this.generation) return;
        this.preview = response.data; this.previewStale = false; this.reviewedInput = input;
        for (const row of this.preview.rows || []) if (row.existingId && !this.actions[row.row]) this.actions[row.row] = "";
      } catch (error) { if (generation === this.generation) this.error = paymentTermImportError(error); }
      finally { if (generation === this.generation) this.busy = false; }
    },
    decideAll(action) {
      for (const row of this.preview?.rows || []) if (row.existingId) this.actions[row.row] = action;
      this.invalidatePreview();
    },
    async applyImport() {
      if (!this.canWrite || !this.canRead || !this.isCurrentCompany || this.busy || this.previewStale || !this.reviewedInput || !this.preview?.previewToken) return;
      const generation = ++this.generation;
      const input = { ...this.reviewedInput, previewToken: this.preview.previewToken };
      this.busy = true; this.error = "";
      try {
        const response = await CompanyService.importPaymentTerms(this.companyId, input);
        if (generation !== this.generation) return;
        this.result = response.data; this.preview = null; this.reviewedInput = null; this.content = ""; this.actions = {}; this.fileKey++;
        this.$emit("imported");
      } catch (error) {
        if (generation === this.generation) { this.error = paymentTermImportError(error); this.invalidatePreview(); }
      } finally { if (generation === this.generation) this.busy = false; }
    },
    async exportFile() {
      if (!this.canRead || !this.isCurrentCompany || this.busy) return;
      const generation = ++this.generation; this.busy = true; this.error = "";
      try {
        const response = await CompanyService.exportPaymentTerms(this.companyId);
        if (generation !== this.generation) return;
        const url = URL.createObjectURL(new Blob([response.data], { type: "application/json" }));
        const link = document.createElement("a"); link.href = url; link.download = "zahlungsbedingungen.json";
        link.click(); URL.revokeObjectURL(url);
      } catch (error) {
        const message = await paymentTermExportError(error);
        if (generation === this.generation) this.error = message;
      }
      finally { if (generation === this.generation) this.busy = false; }
    },
    actionLabel(action) { return { create: "Neu anlegen", update: "Aktualisieren", skip: "Überspringen", invalid: "Ungültig" }[action] || action; },
  },
};
</script>
